import { ThemeColor } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/theme/themeHelpers';
import { hexAlphaToTailwindOpacity } from '@mutiny-pkg/tailwind-parser';
import { LexicalThemeColors } from '../richtext/types';

/**
 * Converts a hex color (with optional alpha) to Tailwind className format.
 * Matches against theme colors when possible, falls back to custom hex format.
 *
 * @param hexColor - Hex color with optional alpha (e.g., '#FF0000' or '#FF0000E6')
 * @param themeColors - Array of theme colors to match against
 * @param prefix - Tailwind prefix (e.g., 'text-', 'bg-')
 * @returns Tailwind class (e.g., 'text-primary/90' or 'text-[#FF0000]/90')
 */
export function convertHexToTailwindClass(
  hexColor: string,
  themeColors: ThemeColor[],
  prefix: 'text-' | 'bg-' | 'hover:text-' | 'hover:bg-',
): string {
  if (!hexColor) {
    return '';
  }

  const hasAlpha = hexColor.length === 9 && hexColor.startsWith('#');
  const baseColor = hasAlpha ? hexColor.slice(0, 7) : hexColor;
  const alphaHex = hasAlpha ? hexColor.slice(7, 9) : undefined;

  let matchingThemeColor = themeColors.find((c) => c.colorValue.toLowerCase() === baseColor.toLowerCase());

  if (!matchingThemeColor && baseColor.length === 4) {
    const expandedColor = `#${baseColor[1]}${baseColor[1]}${baseColor[2]}${baseColor[2]}${baseColor[3]}${baseColor[3]}`;
    matchingThemeColor = themeColors.find((c) => c.colorValue.toLowerCase() === expandedColor.toLowerCase());
  }

  let className = '';

  if (matchingThemeColor && matchingThemeColor.tokenName) {
    className = `${prefix}${matchingThemeColor.tokenName}`;
  } else {
    className = `${prefix}[${baseColor}]`;
  }

  if (alphaHex && alphaHex !== 'FF') {
    const opacity = hexAlphaToTailwindOpacity(alphaHex);
    className = `${className}/${opacity}`;
  }

  return className;
}

/**
 * Converts LexicalThemeColors to ThemeColor[] format for use with conversion functions.
 *
 * @param lexicalColors - LexicalThemeColors object from the theme
 * @returns Array of ThemeColor objects
 */
export function lexicalThemeColorsToThemeColors(lexicalColors?: LexicalThemeColors): ThemeColor[] {
  if (!lexicalColors) {
    return [];
  }

  const colors: ThemeColor[] = [];

  Object.entries(lexicalColors.color).forEach(([key, value]) => {
    if (value && key !== 'none' && key !== 'unset') {
      colors.push({
        label: key,
        value: key,
        colorValue: value,
        tokenName: key === 'textDark' ? 'dark' : key === 'textLight' ? 'light' : key,
      });
    }
  });

  if (lexicalColors.additionalColors) {
    lexicalColors.additionalColors.forEach((colorValue) => {
      colors.push({
        label: colorValue,
        value: colorValue,
        colorValue,
      });
    });
  }

  return colors;
}
